// @givmo/org-console — what an invite OFFERS, as a draft the composer edits and a body it submits.
//
// ══ WHY THIS IS A MODULE AND NOT A FORM'S useState ═════════════════════════════════════════════
//
// `org-access-model.ts` exists because the wire shape of an access change is a three-valued diff no
// form should compute inline. This one exists for a different reason: the offer's rules are the
// SERVER's, they are re-checked at redemption, and a composer that lets a Global Admin submit an
// offer the server will refuse has already spent their attention on a control that could not work.
//
// So the same two rules live here, as pure functions over a draft:
//
//   * **A Global-Admin invite carries NO apps.** That role's access is every app at Owner level, so a
//     list beside it would be a second, contradictory answer to what the person gets. The form does
//     not merely hide the list for that role — {@link buildInviteBody} emits an empty one, so a draft
//     that was edited and then switched cannot leak a stale selection onto the wire.
//   * **A Member invite's apps ARE their access.** Under the invitation model nothing fans out behind
//     the list, so what is chosen here is the whole of what they get.
//
// ⚠ ONE RULE HERE IS STRICTER THAN THE SERVER'S, DELIBERATELY: a Member offer with no apps is
// ACCEPTED by the backend (a real, if useless, state under the invitation model) and REFUSED by this
// form. Inviting someone to administer nothing is not an act a person means to perform — it costs
// them an email, an expiry and a redemption to arrive holding nothing — so the composer names the
// gap instead of submitting it. This is the only place the two disagree, and it is a narrowing: no
// offer this form can build is one the server would refuse for its content.

import type {
  CreateOrgInviteBody,
  OrgModuleGrantOffer,
  OrgModuleCatalogRow,
  OrgSelfAccess,
} from './wire-types';
import { ROLE_MEMBER, ROLE_OWNER, moduleTier, type OrgTier } from './org-tiers';

/**
 * Email format.
 *
 * ⚠ A THIRD COPY OF ONE REGEX, and it is the honest disposition rather than a miss. The rule is the
 * webapp's `Regexes.emailValid`, already carried verbatim by two of this repo's apps in their own
 * `auth-schemas.ts` — a duplication that predates this file. A `packages/` member cannot import
 * from an app, and promoting a shared auth schema out of two apps is a change to both of their
 * sign-in surfaces, which is a different piece of work from adding a composer. Kept byte-identical
 * to both, so a future promotion is a deletion here rather than a reconciliation.
 */
export const INVITE_EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/** One app's row in the composer: whether it is offered, and at what level. */
export interface InviteModuleDraft {
  module: string;
  offered: boolean;
  tier: OrgTier;
}

/** Everything the composer holds. */
export interface InviteDraft {
  email: string;
  /** True ⇒ they are offered Global Admin, and {@link InviteDraft.modules} is not consulted. */
  isGlobalAdmin: boolean;
  modules: InviteModuleDraft[];
  /**
   * MODULE-ISSUED mode (D-CPO-24 clause 7): the app this offer is made from INSIDE — set only by a
   * module's own Team page. When set, the body carries `issued_module`, exactly that one app is
   * offered, and the offer is never Global Admin, whatever else the draft holds.
   */
  issuedModule?: string;
  /**
   * Mark them "External" when they accept (§OM-53). Off by default on both doors: the label is a
   * statement about the person, and nobody is marked because a form guessed.
   */
  external: boolean;
}

/**
 * A blank draft over this organization's catalog — the ORG-WIDE door's starting point.
 *
 * ⚠ THE DEFAULT IS **Member WITH NOTHING CHOSEN**, not Global Admin. The legacy single-module dialog
 * this replaces defaulted to Owner — appropriate when every admin of a one-module portal was an
 * Owner and there was nothing else to be. In a suite the same default hands whoever is invited every
 * app the organization has, at the highest level, by pressing one button. Elevation is a deliberate
 * act; the form starts at the least it can offer and makes the person say what they mean.
 *
 * Nothing is preselected either, for the same reason: a preselected app is an app somebody gets
 * because a form guessed. (A module's own Team page does not pre-select — it opens the narrower
 * MODULE-ISSUED draft, {@link moduleIssuedInviteDraft}, whose one app is the page's own.)
 */
export function emptyInviteDraft(catalog: readonly OrgModuleCatalogRow[]): InviteDraft {
  return {
    email: '',
    isGlobalAdmin: false,
    modules: catalog.map((row) => ({ module: row.module, offered: false, tier: ROLE_MEMBER })),
    external: false,
  };
}

/**
 * The MODULE-ISSUED draft (D-CPO-24 clause 7) — a module's own Team page's starting point.
 *
 * Exactly that one app, already offered, at the default per-app level (Member); no app list to edit
 * and never Global Admin. `issuedModule` is what makes {@link buildInviteBody} stamp
 * `issued_module`, so the server judges the offer under clause 7's scope — at create, and again at
 * accept against the issuer's standing then.
 */
export function moduleIssuedInviteDraft(module: string): InviteDraft {
  const slug = module.trim();
  return {
    ...emptyInviteDraft([]),
    modules: [{ module: slug, offered: true, tier: ROLE_MEMBER }],
    issuedModule: slug,
  };
}

/** The levels an issuer may offer: every tier at or below their own — clause 7's cap. */
export function tiersAtOrBelow(cap: OrgTier): OrgTier[] {
  return cap === ROLE_OWNER ? [ROLE_MEMBER, ROLE_OWNER] : [ROLE_MEMBER];
}

/**
 * The issuer's OWN tier in one module, off the standing read — the cap on a module-issued offer
 * ("a tier no higher than the issuer's own"). Read off the module ROW, never off an admin flag: the
 * resolver reports a Global Admin at Owner in every module it holds, which is the same fact the
 * server caps against. A missing or unheld row is Member, the least — a cap never guesses upward.
 */
export function issuerTierIn(self: OrgSelfAccess | null, module: string): OrgTier {
  const slug = module.trim();
  const row = self?.modules.find((candidate) => candidate.module === slug);
  if (row === undefined || row.has_access !== true) return ROLE_MEMBER;
  return moduleTier(row.granted_role);
}

/**
 * Re-seat a draft on a catalog that arrived (or changed) after it was primed.
 *
 * The catalog is a paged read the composer exhausts, so a draft built before it lands has no rows to
 * edit. Choices already made are kept by module slug; a module that has since left the catalog drops
 * out, because offering something the organization's class no longer carries is exactly what the
 * server refuses at create AND again at redemption.
 */
export function reseatInviteDraft(
  draft: InviteDraft,
  catalog: readonly OrgModuleCatalogRow[],
): InviteDraft {
  const chosen = new Map(draft.modules.map((row) => [row.module, row]));
  return {
    ...draft,
    modules: catalog.map(
      (row) => chosen.get(row.module) ?? { module: row.module, offered: false, tier: ROLE_MEMBER },
    ),
  };
}

/** The apps a draft actually offers — empty for a Global-Admin invite, whatever its rows hold. */
export function offeredGrants(draft: InviteDraft): OrgModuleGrantOffer[] {
  if (draft.isGlobalAdmin) return [];
  return draft.modules
    .filter((row) => row.offered)
    .map((row) => ({ module: row.module, granted_role: row.tier }));
}

/** Why a draft cannot be submitted yet. Empty ⇒ it can. */
export type InviteDraftProblem = 'email-missing' | 'email-format' | 'no-apps';

/**
 * Everything wrong with a draft, as codes rather than sentences.
 *
 * Codes, so the rules stay testable as data and the copy stays with the control that shows it —
 * the same split the offer rules use on the backend, and the reason a message is never compared
 * against here.
 */
export function inviteDraftProblems(draft: InviteDraft): InviteDraftProblem[] {
  const problems: InviteDraftProblem[] = [];
  const email = draft.email.trim();
  if (email.length === 0) problems.push('email-missing');
  else if (!INVITE_EMAIL_REGEX.test(email)) problems.push('email-format');
  // See the header: stricter than the server, on purpose, and only here.
  if (!draft.isGlobalAdmin && offeredGrants(draft).length === 0) problems.push('no-apps');
  return problems;
}

/**
 * The request body, or `null` when the draft is not submittable.
 *
 * ⚠ THE EMAIL IS TRIMMED, NOT LOWER-CASED. The server normalizes it (that normalized form is what
 * the redemption's address binding compares against and what the live-invite uniqueness keys on), so
 * folding case here would only make this form's idea of the address differ from the record's for no
 * gain. Trimming is different: a trailing space is a typing artifact, never an address.
 *
 * ⚠ `issued_module` IS SET BY THE RULE, NEVER BY A FORM FIELD. An ORG-WIDE draft (no
 * {@link InviteDraft.issuedModule}) never carries it: that door's author holds Global-Admin
 * authority over every app, and an organization-wide offer arriving with a module's scope would be
 * judged under a scope it was not made under. A MODULE-ISSUED draft (D-CPO-24 clause 7 — a module's
 * own Team page) ALWAYS carries it, and the rest of that body comes from the rule as well: exactly
 * the issued app's grant, never Global Admin, whatever the form held — so the wire shape the server
 * checks (at create, and again at accept) can never disagree with the mode the draft was opened in.
 */
export function buildInviteBody(draft: InviteDraft): CreateOrgInviteBody | null {
  if (inviteDraftProblems(draft).length > 0) return null;
  const issued = (draft.issuedModule ?? '').trim();
  // The mark is in the body only when it is on, so an unmarked invite's body is the one it always was.
  const external = draft.external ? { external: true } : {};
  if (issued.length > 0) {
    return {
      email: draft.email.trim(),
      role: ROLE_MEMBER,
      module_grants: draft.modules
        .filter((row) => row.offered && row.module === issued)
        .map((row) => ({ module: row.module, granted_role: row.tier })),
      issued_module: issued,
      ...external,
    };
  }
  return {
    email: draft.email.trim(),
    role: draft.isGlobalAdmin ? ROLE_OWNER : ROLE_MEMBER,
    module_grants: offeredGrants(draft),
    ...external,
  };
}
